// components/LandMap.js
// ─────────────────────────────────────────────────────────────────────────────
// An ASCII map of the portfolio. Every project is a land. Lands spiral out
// from the capital (the homepage) in order of post date, so the centre is the
// oldest work and the edge is the frontier. Only lands the visitor has opened
// are drawn; everything else is blank until it is found. Ink only — each
// land's texture comes from the first letter of its name.
//
// The layout is deterministic — the same map for every visitor, never
// reshaped — so "discovering" a land means something. Drawn on a canvas that
// fills the logo layer; drag or scroll to pan.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'wk-landmap';
const LEGACY_KEY  = 'wk-heraldic-tree'; // the family tree this replaced

const HOME_ID = 'home';
const CELL_H  = 14;                      // px per text row; column width is measured
const MONO    = '12px ui-monospace, Menlo, Consolas, monospace';
const LABEL   = '500 11px "Solina", Inter, ui-sans-serif, sans-serif';
const INK     = '#333333';
const TRAIL   = '#d0342c';               // the one colour on the map: the visitor's route

// Sections that are lands. Teaching has no case studies, so it isn't mapped.
const MAPPED = new Set(['commercial', 'editorial']);

// Ink only, no colour: a land's texture comes from the first letter of its
// name, so lands sharing an initial share a pattern. (+ - | are borders.)
const FILLS = ['·', ':', '~', "'", '"', '^', '°', '*', ';', ',', '=', '`', '´',
               '¨', '∙', '∘', '⋅', '≈', '∼', '⁘', '⁙', '⁚', '⁖', '∗', '⋆', '∷'];
const fillFor = (land) => {
  if (land.id === HOME_ID) return '·';
  const c = (land.title.trim().toUpperCase().charCodeAt(0) || 65) - 65;
  return FILLS[((c % FILLS.length) + FILLS.length) % FILLS.length];
};
const HOME_FONT = '600 13px "Solina", Inter, ui-sans-serif, sans-serif';
const LINE_H = 14, CREST = 48, CREST_GAP = 6;
const LOGO_W = 230, LOGO_H = 141;         // the SVG wordmark, drawn as the capital


// Deterministic per-cell noise so the coastlines are rough but stable.
function noise(x, y, seed) {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function rgba(hex, a) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export default class LandMap {
  /**
   * @param {object} opts
   * @param {Element} opts.mount      element the map fills (position: relative/absolute parent)
   * @param {Array}   opts.lands      [{ id, title, section, date, weight, crest, url }]
   * @param {string}  [opts.logo]      URL of the wordmark SVG, drawn as the capital
   * @param {Function} [opts.onLandClick]  called with the land when a revealed land is clicked
   */
  constructor({ mount, lands, logo, onLandClick } = {}) {
    this._mount = mount;
    this._onLandClick = onLandClick || null;
    this._logo = null;
    this._logoHalo = null;
    if (logo) {                     // URL of the wordmark SVG, drawn as the capital
      this._logo = new Image();
      this._logo.onload = () => { this._logoHalo = this._makeHalo(this._logo, LOGO_W, LOGO_H, 3); this._requestDraw(); };
      this._logo.src = logo;
    }
    this._store = LandMap._load();

    this._images = new Map();   // crest url -> Image
    this._view = { x: 0, y: 0 }; // world px at the top-left of the viewport
    this._hover = -1;
    this._visible = false;
    this._drag = null;
    this._raf = 0;

    this._buildDOM();
    this._measure();
    this._lands = this._prepare(lands);
    this._layout();
    this._computeTrail();
    this._centreOnHome();
    this._bind();
    this._requestDraw();
    // Glyphs drawn before the page's fonts settle can sit a little wide; repaint after
    if (document.fonts) document.fonts.ready.then(() => this._requestDraw()).catch(() => {});
  }

  /* ── persistence ─────────────────────────────────────────────────────── */

  static _load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.visited) return d;
      }
    } catch (_) {}
    const d = { version: 1, visited: {} };
    // Carry over anything the old family tree recorded
    try {
      const old = JSON.parse(localStorage.getItem(LEGACY_KEY) || 'null');
      if (old && old.projects) {
        for (const id of Object.keys(old.projects)) {
          d.visited[id] = old.projects[id].firstVisitAt || Date.now();
        }
      }
    } catch (_) {}
    return d;
  }

  static _save(d) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch (_) {}
  }

  /** Record a visit without a map on the page (direct case study landings). */
  static recordVisit(id) {
    if (!id) return;
    const d = LandMap._load();
    if (!d.visited[id]) {
      d.visited[id] = Date.now();
      LandMap._save(d);
    }
  }

  /* ── public API ──────────────────────────────────────────────────────── */

  reveal(id) {
    if (!id || id === HOME_ID) return;
    const key = String(id);
    if (this._store.visited[key]) return;
    this._store.visited[key] = Date.now();
    LandMap._save(this._store);
    const land = this._lands.find((l) => l.id === key);
    if (land && land.crest) this._loadImage(land.crest);
    this._computeTrail();
    this._draw(); // straight away — this may happen while the tab is hidden and rAF is paused
  }

  isRevealed(land) {
    return land.id === HOME_ID || !!this._store.visited[land.id];
  }

  get discovered() {
    return this._lands.filter((l) => l.id !== HOME_ID && this.isRevealed(l)).length;
  }

  get total() {
    return this._lands.length - 1;
  }

  show() {
    if (this._visible) return;
    this._visible = true;
    this._root.classList.add('is-visible');
    this._measure();
    this._draw(); // paint immediately so nothing blank shows during the fade-in
  }

  hide() {
    if (!this._visible) return;
    this._visible = false;
    this._root.classList.remove('is-visible', 'is-over-land');
    this._hover = -1;
  }

  handleResize() {
    this._measure();
    if (this._panned) this._clampView();
    else this._centreOnHome(); // until the visitor pans, keep the capital in the middle
    this._draw(); // now, not on the next frame — resizing the canvas blanks it
  }

  /* ── DOM ─────────────────────────────────────────────────────────────── */

  _buildDOM() {
    const root = document.createElement('div');
    root.className = 'landmap';
    const canvas = document.createElement('canvas');
    root.appendChild(canvas);
    this._mount.appendChild(root);
    this._root = root;
    this._canvas = canvas;
    this._ctx = canvas.getContext('2d');
    canvas.__map = this; // handy for inspecting the live instance from devtools
  }

  _measure() {
    const dpr = window.devicePixelRatio || 1;
    const w = this._root.clientWidth || window.innerWidth;
    const h = this._root.clientHeight || window.innerHeight;
    this._vw = w;
    this._vh = h;
    this._canvas.width = Math.round(w * dpr);
    this._canvas.height = Math.round(h * dpr);
    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._ctx.font = MONO;
    // Measure the grid on a detached canvas: a canvas in the page reports a
    // different advance for the mono font early in the load and only settles
    // later, which would size the grid differently from visit to visit.
    const probe = LandMap._probe || (LandMap._probe = document.createElement('canvas').getContext('2d'));
    probe.font = MONO;
    this.cw = probe.measureText('M').width || 7.2; // column width of the mono font
    this.bw = probe.measureText('═').width || this.cw; // box glyphs may come from a fallback font
  }

  /* ── layout ──────────────────────────────────────────────────────────── */

  _prepare(input) {
    const home = {
      id: HOME_ID, title: 'Will Knight', section: 'home',
      date: '', weight: 0, crest: null, url: '/',
    };
    const lands = (input || [])
      .filter((l) => l && l.id && MAPPED.has(l.section))
      .map((l) => ({
        id: String(l.id),
        title: l.title || 'Untitled',
        section: l.section,
        date: l.date || '9999',  // undated lands go to the frontier
        weight: Number(l.weight) || 0,
        crest: l.crest || null,
        url: l.url || null,
      }))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.id < b.id ? -1 : 1));

    for (const l of [home, ...lands]) {
      // The name (and crest) must sit inside the land, so measure them first
      // and let a land be no smaller than its label needs — beyond that it
      // grows with the amount of work in it.
      l.label = this._measureLabel(l);
      const forLabel = Math.hypot(l.label.w / 2 + 12, l.label.h / 2 + 10);
      const forWork = 30 + Math.min(l.weight, 16) * 2.4;
      l.r = Math.round(Math.max(forLabel, forWork, l.id === HOME_ID ? 60 : 0));
      l.fill = fillFor(l);
      if (this.isRevealed(l) && l.crest) this._loadImage(l.crest);
    }
    return [home, ...lands];
  }

  /**
   * A white silhouette of `img` grown by `pad` px on every side — drawn under
   * the image it outlines it, the way a stroke outlines text.
   */
  _makeHalo(img, w, h, pad) {
    const c = document.createElement('canvas');
    c.width = w + pad * 2; c.height = h + pad * 2;
    const x = c.getContext('2d');
    for (let a = 0; a < Math.PI * 2; a += Math.PI / 8) {
      x.drawImage(img, pad + Math.cos(a) * pad, pad + Math.sin(a) * pad, w, h);
    }
    x.globalCompositeOperation = 'source-in';
    x.fillStyle = '#fff';
    x.fillRect(0, 0, c.width, c.height);
    return c;
  }

  /** Wrapped lines and the box (crest + name) a land's label occupies. */
  _measureLabel(land) {
    const ctx = this._ctx;
    const isHome = land.id === HOME_ID;
    if (isHome && this._logo) return { lines: [], w: LOGO_W, h: LOGO_H, logo: true };
    ctx.font = isHome ? HOME_FONT : LABEL;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '1px';
    const lines = this._wrapTitle(ctx, land.title.toUpperCase(), isHome ? 220 : 118, 3);
    const w = Math.max(...lines.map((t) => ctx.measureText(t).width));
    let h = lines.length * LINE_H;
    if (land.crest) h += CREST + CREST_GAP;   // space is held even before the image loads
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
    return { lines, w, h };
  }

  _layout() {
    const lands = this._lands;
    const cw = this.cw, ch = CELL_H;

    // ── 1) Seed positions: an Archimedean spiral out from the capital ──
    // Successive lands sit shoulder to shoulder along the spiral; rings are
    // spaced so neighbouring turns just touch.
    const avgR = lands.reduce((a, l) => a + l.r, 0) / lands.length;
    const ringGap = 2 * avgR * 1.15;
    const b = ringGap / (2 * Math.PI);
    lands[0].x = 0; lands[0].y = 0;
    let theta = 0;
    const r0 = lands[0].r + (lands[1] ? lands[1].r : avgR);
    for (let i = 1; i < lands.length; i++) {
      const r = r0 + b * theta;
      if (i > 1) theta += (lands[i - 1].r + lands[i].r) / Math.max(r, 1); // shoulder to shoulder
      const rr = r0 + b * theta;
      lands[i].x = Math.cos(theta) * rr;
      lands[i].y = Math.sin(theta) * rr;
    }

    // ── 2) World grid in text cells, with a margin ──
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const l of lands) {
      minX = Math.min(minX, l.x - l.r * 1.4); maxX = Math.max(maxX, l.x + l.r * 1.4);
      minY = Math.min(minY, l.y - l.r * 1.4); maxY = Math.max(maxY, l.y + l.r * 1.4);
    }
    const margin = 260;
    this._originX = minX - margin;
    this._originY = minY - margin;
    this._W = Math.ceil((maxX - minX + margin * 2) / cw);
    this._H = Math.ceil((maxY - minY + margin * 2) / ch);
    this._worldW = this._W * cw;
    this._worldH = this._H * ch;
    for (const l of lands) { l.x -= this._originX; l.y -= this._originY; }

    // ── 3) The landmass. First the coast: a cell is land if it lies within
    //      1.35 radii of any land (with per-cell noise, so the edge is rough).
    //      Anything the coast encloses is land too — no lakes between lands —
    //      and then every land cell goes to whichever land is nearest,
    //      measured relative to that land's radius so bigger lands push their
    //      borders out and their labels always have room.
    const W = this._W, H = this._H, N = W * H;
    const idx = new Int16Array(N).fill(-1);
    const mask = new Uint8Array(N);
    const best = new Float32Array(N).fill(Infinity);
    const stamp = new Int32Array(N).fill(-1);
    const queue = new Int32Array(N);
    const dist = (land, cx, cy) =>
      (Math.hypot((cx + 0.5) * cw - land.x, (cy + 0.5) * ch - land.y) + (noise(cx, cy, 7) - 0.5) * 8) / land.r;
    const neighbours = (i, cx, cy, out) => {
      out[0] = cx > 0 ? i - 1 : -1; out[1] = cx < W - 1 ? i + 1 : -1;
      out[2] = cy > 0 ? i - W : -1; out[3] = cy < H - 1 ? i + W : -1;
    };
    const nb = [0, 0, 0, 0];

    // 3a) Coast
    lands.forEach((land, k) => {
      const sx = Math.floor(land.x / cw), sy = Math.floor(land.y / ch);
      let head = 0, tail = 0;
      queue[tail++] = sy * W + sx; stamp[sy * W + sx] = k;
      while (head < tail) {
        const i = queue[head++];
        const cx = i % W, cy = (i - cx) / W;
        if (dist(land, cx, cy) > 1.35) continue;
        mask[i] = 1;
        neighbours(i, cx, cy, nb);
        for (const j of nb) if (j >= 0 && stamp[j] !== k) { stamp[j] = k; queue[tail++] = j; }
      }
    });

    // 3b) Tidy the coast (majority of the 3×3 block), then fill anything it encloses
    for (let pass = 0; pass < 2; pass++) {
      const before = Uint8Array.from(mask);
      for (let cy = 1; cy < H - 1; cy++) for (let cx = 1; cx < W - 1; cx++) {
        const i = cy * W + cx;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) n += before[i + dy * W + dx];
        mask[i] = n >= 5 ? 1 : 0;
      }
    }
    {
      const outside = new Uint8Array(N);
      let head = 0, tail = 0;
      for (let cx = 0; cx < W; cx++) for (const cy of [0, H - 1]) { const i = cy * W + cx; if (!mask[i] && !outside[i]) { outside[i] = 1; queue[tail++] = i; } }
      for (let cy = 0; cy < H; cy++) for (const cx of [0, W - 1]) { const i = cy * W + cx; if (!mask[i] && !outside[i]) { outside[i] = 1; queue[tail++] = i; } }
      while (head < tail) {
        const i = queue[head++];
        const cx = i % W, cy = (i - cx) / W;
        neighbours(i, cx, cy, nb);
        for (const j of nb) if (j >= 0 && !mask[j] && !outside[j]) { outside[j] = 1; queue[tail++] = j; }
      }
      for (let i = 0; i < N; i++) if (!mask[i] && !outside[i]) mask[i] = 1; // an enclosed hole
    }

    // 3c) Assign every land cell to its nearest land
    stamp.fill(-1);
    lands.forEach((land, k) => {
      const sx = Math.floor(land.x / cw), sy = Math.floor(land.y / ch);
      let head = 0, tail = 0;
      queue[tail++] = sy * W + sx; stamp[sy * W + sx] = k;
      while (head < tail) {
        const i = queue[head++];
        if (!mask[i]) continue;
        const cx = i % W, cy = (i - cx) / W;
        const d = dist(land, cx, cy);
        if (d >= best[i]) continue;       // someone nearer already has it
        best[i] = d; idx[i] = k;
        neighbours(i, cx, cy, nb);
        for (const j of nb) if (j >= 0 && stamp[j] !== k) { stamp[j] = k; queue[tail++] = j; }
      }
    });
    for (let i = 0; i < N; i++) {          // anything an expansion never reached
      if (!mask[i] || idx[i] >= 0) continue;
      const cx = i % W, cy = (i - cx) / W;
      let bk = 0, bd = Infinity;
      lands.forEach((land, k) => { const d = dist(land, cx, cy); if (d < bd) { bd = d; bk = k; } });
      idx[i] = bk;
    }

    // 3d) Smooth the borders between lands (majority of the 3×3 block), keeping
    //      the coast exactly as it is so the landmass stays whole.
    for (let pass = 0; pass < 2; pass++) {
      const before = Int16Array.from(idx);
      for (let cy = 1; cy < H - 1; cy++) for (let cx = 1; cx < W - 1; cx++) {
        const i = cy * W + cx;
        if (!mask[i]) continue;
        const me = before[i];
        const counts = new Map();
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const v = before[i + dy * W + dx];
          if (v >= 0) counts.set(v, (counts.get(v) || 0) + 1);
        }
        let top = me, topN = counts.get(me) || 0;
        for (const [v, n] of counts) if (n > topN) { top = v; topN = n; }
        if (top !== me && topN >= 5) idx[i] = top;
      }
    }

    // ── 3c) Each label sits at the centre of its land's bounding box — for a
    //      blob that is what the eye reads as the middle — not at the seed.
    const minCX = new Int32Array(lands.length).fill(W), maxCX = new Int32Array(lands.length).fill(-1);
    const minCY = new Int32Array(lands.length).fill(H), maxCY = new Int32Array(lands.length).fill(-1);
    for (let cy = 0; cy < H; cy++) for (let cx = 0; cx < W; cx++) {
      const k = idx[cy * W + cx];
      if (k < 0) continue;
      if (cx < minCX[k]) minCX[k] = cx; if (cx > maxCX[k]) maxCX[k] = cx;
      if (cy < minCY[k]) minCY[k] = cy; if (cy > maxCY[k]) maxCY[k] = cy;
    }
    lands.forEach((land, k) => {
      if (maxCX[k] < 0) { land.lx = land.x; land.ly = land.y; return; }
      land.lx = ((minCX[k] + maxCX[k] + 1) / 2) * cw;
      land.ly = ((minCY[k] + maxCY[k] + 1) / 2) * ch;
    });

    // ── 4) Reserve each land's label box for that land, so a name can never
    //      be cut by a neighbour's border.
    lands.forEach((land, k) => {
      const { w, h } = land.label;
      const x0 = Math.floor((land.lx - w / 2 - 6) / cw), x1 = Math.ceil((land.lx + w / 2 + 6) / cw);
      const y0 = Math.floor((land.ly - h / 2 - 4) / ch), y1 = Math.ceil((land.ly + h / 2 + 4) / ch);
      for (let cy = Math.max(0, y0); cy < Math.min(H, y1); cy++)
        for (let cx = Math.max(0, x0); cx < Math.min(W, x1); cx++) idx[cy * W + cx] = k;
    });
    this._idx = idx;
  }

  /**
   * The route: capital → each discovered land in the order it was found.
   * Any undiscovered land the route crosses becomes a ghost — its outline
   * and a whisper of texture, but no name and nothing to click.
   */
  _computeTrail() {
    const v = this._store.visited;
    const order = this._lands
      .filter((l) => l.id !== HOME_ID && v[l.id])
      .sort((a, b) => (v[a.id] - v[b.id]) || (a.id < b.id ? -1 : 1));
    this._trail = [this._lands[0], ...order];

    const ghost = new Uint8Array(this._lands.length);
    for (let i = 1; i < this._trail.length; i++) {
      const a = this._trail[i - 1], b = this._trail[i];
      const steps = Math.max(1, Math.ceil(Math.hypot(b.lx - a.lx, b.ly - a.ly) / 4));
      for (let st = 0; st <= steps; st++) {
        const t = st / steps;
        const cx = Math.floor((a.lx + (b.lx - a.lx) * t) / this.cw);
        const cy = Math.floor((a.ly + (b.ly - a.ly) * t) / CELL_H);
        if (cx < 0 || cy < 0 || cx >= this._W || cy >= this._H) continue;
        const k = this._idx[cy * this._W + cx];
        if (k >= 0 && !this.isRevealed(this._lands[k])) ghost[k] = 1;
      }
    }
    this._ghost = ghost;
  }

  _centreOnHome() {
    const home = this._lands[0];
    this._view.x = home.x - this._vw / 2;
    this._view.y = home.y - this._vh / 2;
    this._clampView();
  }

  _clampView() {
    const pad = 120;
    // Whole pixels: fractional offsets make text and lines shimmer while panning
    this._view.x = Math.round(Math.max(-pad, Math.min(this._view.x, this._worldW - this._vw + pad)));
    this._view.y = Math.round(Math.max(-pad, Math.min(this._view.y, this._worldH - this._vh + pad)));
  }

  /* ── input ───────────────────────────────────────────────────────────── */

  _bind() {
    const c = this._canvas;
    c.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this._drag = { x: e.clientX, y: e.clientY, vx: this._view.x, vy: this._view.y, moved: false };
      c.setPointerCapture(e.pointerId);
      this._root.classList.add('is-dragging');
    });
    c.addEventListener('pointermove', (e) => {
      if (this._drag) {
        const dx = e.clientX - this._drag.x, dy = e.clientY - this._drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) { this._drag.moved = true; this._panned = true; }
        this._view.x = this._drag.vx - dx;
        this._view.y = this._drag.vy - dy;
        this._clampView();
        this._requestDraw();
        return;
      }
      this._setHover(this._landAt(e.clientX, e.clientY));
    });
    const end = (e) => {
      if (!this._drag) return;
      const wasClick = !this._drag.moved;
      this._drag = null;
      this._root.classList.remove('is-dragging');
      if (wasClick) {
        const k = this._landAt(e.clientX, e.clientY);
        const land = k >= 0 ? this._lands[k] : null;
        if (land && this.isRevealed(land) && land.id !== HOME_ID && this._onLandClick) {
          this._onLandClick(land);
        }
      }
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', end);
    c.addEventListener('pointerleave', () => { if (!this._drag) this._setHover(-1); });
    c.addEventListener('wheel', (e) => {
      e.preventDefault();
      this._panned = true;
      this._view.x += e.deltaX;
      this._view.y += e.deltaY;
      this._clampView();
      this._requestDraw();
    }, { passive: false });
  }

  _landAt(clientX, clientY) {
    const rect = this._canvas.getBoundingClientRect();
    const wx = clientX - rect.left + this._view.x;
    const wy = clientY - rect.top + this._view.y;
    const cx = Math.floor(wx / this.cw), cy = Math.floor(wy / CELL_H);
    if (cx < 0 || cy < 0 || cx >= this._W || cy >= this._H) return -1;
    const k = this._idx[cy * this._W + cx];
    return k >= 0 && this.isRevealed(this._lands[k]) ? k : -1;
  }

  _setHover(k) {
    if (k === this._hover) return;
    this._hover = k;
    const land = k >= 0 ? this._lands[k] : null;
    this._root.classList.toggle('is-over-land', !!(land && land.url && land.id !== HOME_ID));
    this._requestDraw();
  }

  /* ── drawing ─────────────────────────────────────────────────────────── */

  _loadImage(src) {
    if (this._images.has(src)) return;
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => this._requestDraw();
    img.src = src;
    this._images.set(src, img);
  }

  _requestDraw() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => { this._raf = 0; this._draw(); });
  }

  _draw() {
    // Self-heal if the canvas has changed size under us (a missed resize event
    // would otherwise draw the frame for one width and show it at another)
    if (this._root.clientWidth !== this._vw || this._root.clientHeight !== this._vh) {
      this._measure();
      if (this._panned) this._clampView(); else this._centreOnHome();
    }
    const ctx = this._ctx, cw = this.cw, ch = CELL_H;
    const { x: vx, y: vy } = this._view;
    const W = this._W, H = this._H, idx = this._idx, lands = this._lands;

    ctx.clearRect(0, 0, this._vw, this._vh);
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.font = MONO;

    const c0 = Math.max(0, Math.floor(vx / cw)), c1 = Math.min(W - 1, Math.ceil((vx + this._vw) / cw));
    const r0 = Math.max(0, Math.floor(vy / ch)), r1 = Math.min(H - 1, Math.ceil((vy + this._vh) / ch));

    // ── 1) Territory: texture in every cell, then the outline as a 1px line
    //      along the cell edges — a closed pixel outline that always joins,
    //      where per-cell glyphs left gaps at every one-row step.
    const revealed = lands.map((l) => this.isRevealed(l));
    const ghost = this._ghost || new Uint8Array(lands.length);
    const shown = revealed.map((r, k) => r || !!ghost[k]);
    const at = (cx, cy) => (cx < 0 || cy < 0 || cx >= W || cy >= H) ? -1 : idx[cy * W + cx];
    // A shared edge is drawn once: a discovered land owns its edges against
    // ghosts; between two of a kind, the older land (lower index) owns it.
    const edge = (k, n) => n !== k && (n < 0 || !shown[n]
      || (revealed[k] && !revealed[n]) || (revealed[k] === revealed[n] && k < n));
    // Edge positions come from the column/row index alone, so two cells that
    // share an edge always get the identical pixel for it.
    const ex = (c) => Math.round(c * cw - vx) + 0.5;
    const ey = (r) => Math.round(r * ch - vy) + 0.5;

    const solid = new Path2D(), faint = new Path2D();
    for (let cy = r0; cy <= r1; cy++) {
      for (let cx = c0; cx <= c1; cx++) {
        const k = idx[cy * W + cx];
        if (k < 0 || !shown[k]) continue;
        const land = lands[k];
        const isGhost = !revealed[k];
        const sx = cx * cw - vx, sy = cy * ch - vy;

        if (k === this._hover) { // a faint wash marks the land under the pointer
          ctx.fillStyle = 'rgba(0,0,0,0.05)';
          ctx.fillRect(sx, sy, cw + 0.5, ch + 0.5);
        }
        ctx.fillStyle = rgba(INK, isGhost ? 0.07 : land.id === HOME_ID ? 0.2 : 0.3);
        ctx.fillText(land.fill, sx + cw / 2, sy + ch / 2);

        const path = isGhost ? faint : solid;
        const x0 = ex(cx), x1 = ex(cx + 1), y0 = ey(cy), y1 = ey(cy + 1);
        if (edge(k, at(cx - 1, cy))) { path.moveTo(x0, y0); path.lineTo(x0, y1); }
        if (edge(k, at(cx + 1, cy))) { path.moveTo(x1, y0); path.lineTo(x1, y1); }
        if (edge(k, at(cx, cy - 1))) { path.moveTo(x0, y0); path.lineTo(x1, y0); }
        if (edge(k, at(cx, cy + 1))) { path.moveTo(x0, y1); path.lineTo(x1, y1); }
      }
    }
    ctx.lineWidth = 1;
    ctx.strokeStyle = rgba(INK, 0.25);
    ctx.stroke(faint);
    ctx.strokeStyle = rgba(INK, 0.8);
    ctx.stroke(solid);

    // ── 1b) The trail: a red dash from the capital through every discovery ──
    if (this._trail && this._trail.length > 1) {
      ctx.save();
      ctx.strokeStyle = TRAIL;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      this._trail.forEach((l, i) => (i ? ctx.lineTo(l.lx - vx, l.ly - vy) : ctx.moveTo(l.lx - vx, l.ly - vy)));
      ctx.stroke();
      ctx.restore();
    }

    // ── 2) Labels and crests, centred in their lands ──
    let hot = null;
    for (let k = 0; k < lands.length; k++) {
      const land = lands[k];
      if (!this.isRevealed(land)) continue;
      const sx = land.lx - vx, sy = land.ly - vy;
      if (sx < -land.r || sy < -land.r || sx > this._vw + land.r || sy > this._vh + land.r) continue;
      if (k === this._hover) { hot = { land, sx, sy }; continue; }
      this._drawLabel(land, sx, sy, false);
    }
    if (hot) this._drawLabel(hot.land, hot.sx, hot.sy, true);

    // ── 3) Key, in screen space ──
    this._drawKey();
  }

  _drawLabel(land, sx, sy, hot) {
    const ctx = this._ctx;
    const { lines, w, h } = land.label;
    const isHome = land.id === HOME_ID;
    const crest = land.crest ? this._images.get(land.crest) : null;

    // The capital is the wordmark itself, outlined in white so it sits on the texture
    if (land.label.logo) {
      if (this._logoHalo) ctx.drawImage(this._logoHalo, sx - w / 2 - 3, sy - h / 2 - 3);
      if (this._logo.complete && this._logo.naturalWidth > 0) {
        ctx.drawImage(this._logo, sx - w / 2, sy - h / 2, w, h);
      }
      return;
    }

    // The whole block (crest + name) is centred on the land
    const top = sy - h / 2;
    let y = top;
    if (land.crest) {
      if (crest && crest.complete && crest.naturalWidth > 0) {
        // Fit inside the crest box at its own proportions, centred in it
        const k = Math.min(CREST / crest.naturalWidth, CREST / crest.naturalHeight);
        const cwid = crest.naturalWidth * k, chgt = crest.naturalHeight * k;
        ctx.drawImage(crest, sx - cwid / 2, y + (CREST - chgt) / 2, cwid, chgt);
      }
      y += CREST + CREST_GAP;
    }

    ctx.font = isHome ? HOME_FONT : LABEL;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '1px';
    // A white stroke under the ink so the name reads over the texture
    ctx.lineJoin = 'round';
    ctx.lineWidth = 4;
    ctx.strokeStyle = '#fff';
    lines.forEach((t, i) => ctx.strokeText(t, sx, y + LINE_H / 2 + i * LINE_H));
    ctx.fillStyle = hot ? '#000' : INK;
    lines.forEach((t, i) => ctx.fillText(t, sx, y + LINE_H / 2 + i * LINE_H));
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
  }

  /** Break a title into up to `maxLines` lines that fit `maxW`; ellipsise only if it still won't. */
  _wrapTitle(ctx, text, maxW, maxLines = 2) {
    const fits = (t) => ctx.measureText(t).width <= maxW;
    const clip = (t) => { while (t.length > 3 && !fits(t + '…')) t = t.slice(0, -1); return t.trimEnd() + '…'; };
    const words = text.split(/\s+/).filter(Boolean);
    const lines = [];
    let cur = '';
    for (let i = 0; i < words.length; i++) {
      const wd = words[i];
      const next = cur ? `${cur} ${wd}` : wd;
      if (fits(next)) { cur = next; continue; }
      if (cur) lines.push(cur);
      if (lines.length === maxLines - 1) {          // last line takes whatever is left
        const rest = words.slice(i).join(' ');
        lines.push(fits(rest) ? rest : clip(rest));
        return lines;
      }
      cur = fits(wd) ? wd : clip(wd);
    }
    if (cur) lines.push(cur);
    return lines.length ? lines : [text];
  }

  /** Draw a run of glyphs one per cell, so the run's length never depends on the font's own advance. */
  _drawRun(text, x, y, step) {
    const ctx = this._ctx;
    for (let i = 0; i < text.length; i++) ctx.fillText(text[i], x + i * step, y);
  }

  _drawKey() {
    const ctx = this._ctx, bw = this.bw, ch = CELL_H;
    const text = ` KINGDOMS DISCOVERED  ${this.discovered} / ${this.total} `;
    const inner = text.length;
    const lines = [
      '╔' + '═'.repeat(inner) + '╗',
      '║' + text + '║',
      '╚' + '═'.repeat(inner) + '╝',
    ];
    const x = 8 + bw * 2, y = this._vh - 8 - ch * 5;
    ctx.font = MONO;
    ctx.textAlign = 'left';
    ctx.fillStyle = '#fff';
    ctx.fillRect(x - 2, y - ch / 2 - 1, (inner + 2) * bw + 4, ch * 3 + 2);
    ctx.fillStyle = rgba(INK, 0.85);
    lines.forEach((ln, i) => this._drawRun(ln, x, y + i * ch, bw));
    ctx.textAlign = 'center';
  }

}
